import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { DBBudgetLineFreelancer } from "@/lib/db-types";

/**
 * VIB-2927: frilanser på ekstern budsjettlinje. Skriver bare til
 * budget_line_freelancers, aldri til budget_lines. Prosjektloggen skrives av
 * DB-triggerne (aktør = current_user_id()).
 */

export interface BudgetLineFreelancerRow extends DBBudgetLineFreelancer {
  freelancer: { id: string; name: string; day_rate_nok: number | null } | null;
}

export function useBudgetLineFreelancers(projectId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: ["budget-line-freelancers", projectId],
    enabled: enabled && !!projectId,
    queryFn: async (): Promise<BudgetLineFreelancerRow[]> => {
      const { data, error } = await supabase
        .from("budget_line_freelancers")
        .select("*, freelancer:freelancers(id, name, day_rate_nok)")
        .eq("project_id", projectId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as BudgetLineFreelancerRow[];
    },
  });
}

interface LinkInput {
  budgetLineId: string;
  freelancerId: string;
  projectId: string;
}

export function useAddBudgetLineFreelancer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ budgetLineId, freelancerId, projectId }: LinkInput) => {
      const { error } = await supabase
        .from("budget_line_freelancers")
        .insert({ budget_line_id: budgetLineId, freelancer_id: freelancerId, project_id: projectId });
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "budget_line_freelancers", { projectId: vars.projectId, freelancerId: vars.freelancerId });
    },
    onError: (err: Error) => toast.error(`Kunne ikke sette frilanser: ${err.message}`),
  });
}

export function useRemoveBudgetLineFreelancer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ budgetLineId, freelancerId }: LinkInput) => {
      const { error } = await supabase
        .from("budget_line_freelancers")
        .delete()
        .eq("budget_line_id", budgetLineId)
        .eq("freelancer_id", freelancerId);
      if (error) throw error;
    },
    onSuccess: async (_d, vars) => {
      await invalidateFor(qc, "budget_line_freelancers", { projectId: vars.projectId, freelancerId: vars.freelancerId });
    },
    onError: (err: Error) => toast.error(`Kunne ikke fjerne frilanser: ${err.message}`),
  });
}
